import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { getLgasForState, lgasByState } from "@/data/lgas";
import { sanitizeField, sanitizeName } from "@/lib/sanitize";
import type {
  ProspectCategory,
  ProspectFormData,
  ProspectFormErrors,
  ProspectSubmitPayload,
  ProspectType,
} from "@/types";

const PROSPECT_NAME_MAX_LENGTH = 100;

function isProspectNameInputAllowed(value: string): boolean {
  return /^[\p{L}\p{M} .'’-]*$/u.test(value);
}

function normalizeProspectName(value: string): string | null {
  return isProspectNameInputAllowed(value) && /\p{L}/u.test(value)
    ? sanitizeName(value)
    : null;
}

function isNigerianPhoneInputAllowed(value: string): boolean {
  if (!/^\+?[\d\s().-]*$/.test(value.trim())) return false;

  const digits = value.replace(/\D/g, "");
  const maxDigits = digits.startsWith("234")
    ? 13
    : digits.startsWith("0")
      ? 11
      : 10;
  return digits.length <= maxDigits;
}

function normalizeNigerianPhone(value: string): string | null {
  const input = value.trim();
  if (!isNigerianPhoneInputAllowed(input)) return null;

  const phone = parsePhoneNumberFromString(input, {
    defaultCountry: "NG",
    extract: false,
  });
  return phone?.country === "NG" && phone.isValid() ? phone.number : null;
}

function switchProspectType(
  formData: ProspectFormData,
  prospectType: ProspectType
): ProspectFormData {
  return {
    ...formData,
    prospectType,
    ...(prospectType === "sales_agent" ? { categoryId: "", deviceId: "" } : {}),
  };
}

function validateProspectForm(
  formData: ProspectFormData,
  categories: ProspectCategory[]
): ProspectFormErrors {
  const errors: ProspectFormErrors = {};

  const firstName = normalizeProspectName(formData.firstName);
  const lastName = normalizeProspectName(formData.lastName);

  if (!formData.firstName.trim()) {
    errors.firstName = "First name is required";
  } else if (!firstName) {
    errors.firstName =
      "Enter a name using letters, spaces, apostrophes, hyphens or periods";
  }
  if (!formData.lastName.trim()) {
    errors.lastName = "Last name is required";
  } else if (!lastName) {
    errors.lastName =
      "Enter a name using letters, spaces, apostrophes, hyphens or periods";
  } else if (
    firstName &&
    `${firstName} ${lastName}`.length > PROSPECT_NAME_MAX_LENGTH
  ) {
    errors.lastName = `First and last name together must be ${PROSPECT_NAME_MAX_LENGTH} characters or fewer`;
  }
  if (!formData.phoneNumber.trim()) {
    errors.phoneNumber = "Phone number is required";
  } else if (!normalizeNigerianPhone(formData.phoneNumber)) {
    errors.phoneNumber = "Enter a valid Nigerian phone number";
  }
  if (!formData.nin.trim()) {
    errors.nin = "NIN is required";
  } else if (!/^\d{11}$/.test(formData.nin.trim())) {
    errors.nin = "NIN must be 11 digits";
  }
  if (!sanitizeField("address", formData.address)) {
    errors.address = "Address is required";
  }

  const state = formData.state.trim();
  const lga = formData.lga.trim();
  if (!state) {
    errors.state = "State is required";
  } else if (!Object.hasOwn(lgasByState, state)) {
    errors.state = "Select a valid state";
  }
  if (!lga) {
    errors.lga = "LGA is required";
  } else if (errors.state || !getLgasForState(state).includes(lga)) {
    errors.lga = "Select an LGA in your chosen state";
  }

  if (formData.prospectType === "customer") {
    if (!formData.deviceId.trim()) {
      errors.deviceId = "Select a device of interest";
    } else if (
      !categories.some((category) =>
        category.assets.some((asset) => asset.deviceId === formData.deviceId)
      )
    ) {
      errors.deviceId = "Select an available device";
    }
  }

  return errors;
}

function createProspectPayload(
  formData: ProspectFormData
): ProspectSubmitPayload {
  const firstName = normalizeProspectName(formData.firstName);
  if (!firstName) throw new Error("Enter a valid first name");
  const lastName = normalizeProspectName(formData.lastName);
  if (!lastName) throw new Error("Enter a valid last name");
  const customerName = `${firstName} ${lastName}`;
  if (customerName.length > PROSPECT_NAME_MAX_LENGTH) {
    throw new Error(
      `First and last name together must be ${PROSPECT_NAME_MAX_LENGTH} characters or fewer`
    );
  }

  const phoneNumber = normalizeNigerianPhone(formData.phoneNumber);
  if (!phoneNumber) throw new Error("Enter a valid Nigerian phone number");

  const sharedFields = {
    customerName,
    phoneNumber,
    nin: formData.nin.trim(),
    address: sanitizeField("address", formData.address),
    state: formData.state.trim(),
    lga: formData.lga.trim(),
  };

  return formData.prospectType === "customer"
    ? { ...sharedFields, prospectType: "customer", deviceId: formData.deviceId }
    : { ...sharedFields, prospectType: "sales_agent" };
}

export {
  PROSPECT_NAME_MAX_LENGTH,
  isProspectNameInputAllowed,
  isNigerianPhoneInputAllowed,
  normalizeNigerianPhone,
  switchProspectType,
  validateProspectForm,
  createProspectPayload,
};
